const chapter = {
  id: 'capacitance',
  slug: 'capacitance',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Capacitance',
        hindi: 'धारिता',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Electromagnetism', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'basics',
      title: {
        english: 'Capacitance & Standard Capacitors',
        hindi: 'धारिता और मानक संधारित्र',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Capacitance',
        hindi: 'धारिता',
          },
          body: {
            english: 'Ratio of charge stored to potential difference: $C = Q/V$. SI unit: **farad** (F) = C/V. Common: $\\mu$F, pF.',
        hindi: 'संग्रहीत चार्ज का संभावित अंतर से अनुपात: $C = Q/V$। एसआई इकाई: **फैराड** (एफ) = C/V। सामान्य: $\\mu$F, pF.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Isolated sphere** of radius $R$: $C = 4\\pi\\varepsilon_0 R$.',
        hindi: '**पृथक गोला** त्रिज्या $R$: $C = 4\\pi\\varepsilon_0 R$।',
            },
            {
              english: '**Parallel-plate** (area $A$, separation $d$, vacuum): $C = \\varepsilon_0 A / d$.',
        hindi: '**समानांतर-प्लेट** (क्षेत्र $A$, पृथक्करण $d$, निर्वात): $C = \\varepsilon_0 A / d$।',
            },
            {
              english: '**Spherical capacitor** (concentric shells $a < b$): $C = 4\\pi\\varepsilon_0 \\dfrac{ab}{b-a}$.',
        hindi: '**गोलाकार संधारित्र** (गाढ़ा कोश $a < b$): $C = 4\\pi\\varepsilon_0 \\dfrac{ab}{b-a}$।',
            },
            {
              english: '**Cylindrical capacitor** (length $L$, radii $a, b$): $C = \\dfrac{2\\pi\\varepsilon_0 L}{\\ln(b/a)}$.',
        hindi: '**बेलनाकार संधारित्र** (लंबाई $L$, त्रिज्या $a, b$): $C = \\dfrac{2\\pi\\varepsilon_0 L}{\\ln(b/a)}$।',
            },
          ],
        },
      ],
    },
    {
      id: 'combinations',
      title: {
        english: 'Combinations of Capacitors',
        hindi: 'संधारित्रों के संयोजन',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Series & parallel',
        hindi: 'श्रृंखला और समानांतर',
          },
          statement: {
            english: `**Series**: $\\dfrac{1}{C_{\\text{eq}}} = \\sum \\dfrac{1}{C_i}$. Same charge on each. Voltage divides.
**Parallel**: $C_{\\text{eq}} = \\sum C_i$. Same voltage. Charge divides.`,
            hindi: '**श्रृंखला**: $\\dfrac{1}{C_{\\text{eq}}} = \\sum \\dfrac{1}{C_i}$। हर एक पर एक जैसा चार्ज. वोल्टेज विभाजित होता है. **समानांतर**: $C_{\\text{eq}} = \\sum C_i$. वही वोल्टेज. चार्ज विभाजित होता है.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Series — smallest capacitor stores least charge proportionally? Actually: **same** $Q$ on each (since charge is conserved), so $V_i = Q/C_i$ is largest across smallest $C$.',
        hindi: 'श्रृंखला - सबसे छोटा संधारित्र आनुपातिक रूप से सबसे कम चार्ज संग्रहीत करता है? दरअसल: प्रत्येक पर **समान** $Q$ (चूंकि चार्ज संरक्षित है), इसलिए $V_i = Q/C_i$ सबसे छोटे $C$ में सबसे बड़ा है।',
            },
            {
              english: 'Useful when high voltage required → series; high capacitance → parallel.',
        hindi: 'उच्च वोल्टेज की आवश्यकता होने पर उपयोगी → श्रृंखला; उच्च धारिता → समानांतर।',
            },
          ],
        },
      ],
    },
    {
      id: 'energy',
      title: {
        english: 'Energy Stored & Energy Density',
        hindi: 'संचित ऊर्जा और ऊर्जा घनत्व',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Energy in capacitor',
        hindi: 'संधारित्र में ऊर्जा',
          },
          statement: {
            english: '$U = \\dfrac{1}{2} C V^2 = \\dfrac{1}{2} \\dfrac{Q^2}{C} = \\dfrac{1}{2} Q V$.',
        hindi: '$U = \\dfrac{1}{2} C V^2 = \\dfrac{1}{2} \\dfrac{Q^2}{C} = \\dfrac{1}{2} Q V$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Energy density** in field: $u = \\tfrac12 \\varepsilon_0 E^2$ (vacuum), $\\tfrac12 \\varepsilon E^2 = \\tfrac12 K\\varepsilon_0 E^2$ in dielectric.',
        hindi: '**ऊर्जा घनत्व** क्षेत्र में: $u = \\tfrac12 \\varepsilon_0 E^2$ (वैक्यूम), $\\tfrac12 \\varepsilon E^2 = \\tfrac12 K\\varepsilon_0 E^2$ ढांकता हुआ में।',
            },
            {
              english: 'Charging from a battery: half the energy is **dissipated** as heat (or radiation/loss in wires); only half stored.',
        hindi: 'बैटरी से चार्ज करना: आधी ऊर्जा गर्मी के रूप में **व्यय** हो जाती है (या तारों में radiation/loss); केवल आधा संग्रहित। **आवेशों का साझाकरण**: जब दो कैपेसिटर radiation/loss पर जुड़े होते हैं, तो सामान्य क्षमता , ऊर्जा हानि होती है।',
            },
            {
              english: '**Sharing of charges**: when two capacitors $C_1, C_2$ at $V_1, V_2$ are connected, common potential $V = (C_1 V_1 + C_2 V_2)/(C_1 + C_2)$, energy loss $= \\dfrac{C_1 C_2 (V_1 - V_2)^2}{2(C_1 + C_2)}$.',
        hindi: '**आवेशों का बंटवारा**: जब दो कैपेसिटर $C_1, C_2$ $V_1, V_2$ पर जुड़े होते हैं, तो सामान्य क्षमता $V = (C_1 V_1 + C_2 V_2)/(C_1 + C_2)$, ऊर्जा हानि $= \\dfrac{C_1 C_2 (V_1 - V_2)^2}{2(C_1 + C_2)}$ होती है।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A 4 $\\mu F$ capacitor charged to 100 V is connected in parallel to an uncharged 6 $\\mu F$ capacitor. Common voltage and energy lost.',
        hindi: '100 V पर चार्ज किया गया एक 4 $\\mu F$ संधारित्र एक अनावेशित 6 $\\mu F$ संधारित्र के समानांतर जुड़ा हुआ है। सामान्य वोल्टेज और ऊर्जा नष्ट हो गई।',
          },
          solution: {
            english: '$V = (4 \\cdot 100 + 6 \\cdot 0)/10 = 40$ V. Energy lost $= 4 \\cdot 6 \\cdot 100^2/(2\\cdot 10) = 1200\\ \\mu$J $= 1.2$ mJ.',
        hindi: '$V = (4 \\cdot 100 + 6 \\cdot 0)/10 = 40$ V. ऊर्जा की हानि $= 4 \\cdot 6 \\cdot 100^2/(2\\cdot 10) = 1200\\ \\mu$J $= 1.2$ mJ।',
          },
        },
      ],
    },
    {
      id: 'dielectrics',
      title: {
        english: 'Dielectrics',
        hindi: 'परावैद्युत',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Dielectric constant',
        hindi: 'ढांकता हुआ स्थिरांक',
          },
          body: {
            english: '$K$ (or $\\varepsilon_r$): ratio by which capacitance increases when dielectric fills capacitor. $C\' = K C_0$.',
        hindi: '$K$ (या $\\varepsilon_r$): वह अनुपात जिसके द्वारा ढांकता हुआ संधारित्र भरने पर धारिता बढ़ जाती है। $C\\\' = K C_0$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Polarization $\\vec P = \\varepsilon_0(K - 1)\\vec E$.',
        hindi: 'ध्रुवीकरण $\\vec P = \\varepsilon_0(K - 1)\\vec E$.',
            },
            {
              english: 'Field inside dielectric reduced: $E\' = E_0/K$. Bound surface charge density $\\sigma_b = \\sigma(1 - 1/K)$.',
        hindi: 'ढांकता हुआ अंदर क्षेत्र कम हो गया: $E\\\' = E_0/K$। परिबद्ध सतह आवेश घनत्व $\\sigma_b = \\sigma(1 - 1/K)$।',
            },
            {
              english: 'Battery connected (V const): $C \\to KC$, $Q \\to KQ$, $U \\to KU$.',
        hindi: 'बैटरी कनेक्टेड (V const): $C \\to KC$, $Q \\to KQ$, $U \\to KU$।',
            },
            {
              english: 'Battery disconnected (Q const): $C \\to KC$, $V \\to V/K$, $U \\to U/K$. Mechanical work done by dielectric being pulled in.',
        hindi: 'बैटरी डिस्कनेक्ट हो गई (क्यू स्थिरांक): $C \\to KC$, $V \\to V/K$, $U \\to U/K$। ढांकता हुआ खींचकर किया गया यांत्रिक कार्य।',
            },
            {
              english: 'Slab of thickness $t$, dielectric constant $K$, between plates separation $d$: $C = \\dfrac{\\varepsilon_0 A}{d - t + t/K}$.',
        hindi: 'मोटाई का स्लैब $t$, ढांकता हुआ स्थिरांक $K$, प्लेटों के पृथक्करण के बीच $d$: $C = \\dfrac{\\varepsilon_0 A}{d - t + t/K}$।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Parallel plate capacitor $C_0 = 6\\ \\mu F$. A dielectric of $K = 3$ is inserted to fill the entire space. New capacitance?',
        hindi: 'समानांतर प्लेट संधारित्र $C_0 = 6\\\\ \\mu F$। संपूर्ण स्थान को भरने के लिए $K = 3$ का एक ढांकता हुआ डाला जाता है। नई धारिता?',
          },
          solution: {
            english: '$C = K C_0 = 18\\ \\mu F$.',
        hindi: '$C = K C_0 = 18\\ \\mu F$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'C = Q/V',
      desc: {
        english: 'Definition',
        hindi: 'परिभाषा',
      },
    },
    {
      latex: 'C_{\\text{pp}} = \\varepsilon_0 A / d',
      desc: {
        english: 'Parallel plate',
        hindi: 'समानांतर प्लेट',
      },
    },
    {
      latex: '1/C_s = \\sum 1/C_i,\\ C_p = \\sum C_i',
      desc: {
        english: 'Combinations',
        hindi: 'संचय',
      },
    },
    {
      latex: 'U = \\tfrac12 CV^2 = Q^2/(2C)',
      desc: {
        english: 'Energy stored',
        hindi: 'ऊर्जा संग्रहित',
      },
    },
    {
      latex: 'V_{\\text{common}} = (C_1V_1 + C_2V_2)/(C_1 + C_2)',
      desc: {
        english: 'Sharing',
        hindi: 'साझा करना',
      },
    },
    {
      latex: 'C\\to KC\\ \\text{(dielectric)}',
      desc: {
        english: 'Dielectric effect',
        hindi: 'ढांकता हुआ प्रभाव',
      },
    },
    {
      latex: 'u = \\tfrac12 K\\varepsilon_0 E^2',
      desc: {
        english: 'Energy density',
        hindi: 'ऊर्जा घनत्व',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Energy',
      problem: {
        english: 'A 10 $\\mu F$ capacitor is charged to 200 V. Energy stored?',
        hindi: 'एक 10 $\\mu F$ संधारित्र को 200 V पर चार्ज किया जाता है। ऊर्जा संग्रहित होती है?',
      },
      answer: {
        english: '$0.2$ J',
        hindi: '$0.2$ जे',
      },
      solution: {
        english: '$U = \\tfrac12 C V^2 = \\tfrac12 \\cdot 10^{-5} \\cdot 4\\times 10^4 = 0.2$ J.',
        hindi: '$U = \\tfrac12 C V^2 = \\tfrac12 \\cdot 10^{-5} \\cdot 4\\times 10^4 = 0.2$ J.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Series-parallel',
      problem: {
        english: 'Three 6 $\\mu F$ capacitors in series, then this combination in parallel with another 6 $\\mu F$. Equivalent?',
        hindi: 'श्रृंखला में तीन 6 $\\mu F$ कैपेसिटर, फिर यह संयोजन अन्य 6 $\\mu F$ के साथ समानांतर में। समकक्ष?',
      },
      answer: {
        english: '$8\\ \\mu F$',
        hindi: '$8\\\\ \\mu F$',
      },
      solution: {
        english: 'Series: $6/3 = 2$. Parallel: $2 + 6 = 8$.',
        hindi: 'श्रृंखला: $6/3 = 2$। समानांतर: $2 + 6 = 8$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Dielectric',
      problem: {
        english: 'Capacitor of 5 pF charged with battery to 100 V; battery disconnected; dielectric $K = 2$ inserted. New voltage?',
        hindi: '5 pF का कैपेसिटर 100 V की बैटरी से चार्ज किया गया; बैटरी काट दी गई; ढांकता हुआ $K = 2$ डाला गया। नया वोल्टेज?',
      },
      answer: {
        english: '$50$ V',
        hindi: '$50$ वी',
      },
      solution: {
        english: '$Q$ const, $V \\to V/K$.',
        hindi: '$Q$ स्थिरांक, $V \\to V/K$।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Sharing',
      problem: {
        english: '2 $\\mu F$ at 100 V connects to 3 $\\mu F$ at 0 V. Energy loss?',
        hindi: '100 V पर 2 $\\mu F$ 0 V पर 3 $\\mu F$ से जुड़ता है। ऊर्जा हानि?',
      },
      answer: {
        english: '$6$ mJ',
        hindi: '$6$ एमजे',
      },
      solution: {
        english: '$\\Delta U = (2)(3)(100)^2/(2 \\cdot 5) = 6000\\ \\mu$J $= 6$ mJ.',
        hindi: '$\\Delta U = (2)(3)(100)^2/(2 \\cdot 5) = 6000\\ \\mu$J $= 6$ एमजे।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Plate cap',
      problem: {
        english: 'Distance between plates of a parallel plate capacitor halved with battery still connected. Charge changes by factor?',
        hindi: 'बैटरी अभी भी जुड़ी होने पर समानांतर प्लेट संधारित्र की प्लेटों के बीच की दूरी आधी कर दी गई है। कारक द्वारा प्रभार परिवर्तन?',
      },
      answer: {
        english: '$2$',
        hindi: '$2$',
      },
      solution: {
        english: '$C \\to 2C$, $V$ const, so $Q \\to 2Q$.',
        hindi: '$C \\to 2C$, $V$ स्थिरांक, इसलिए $Q \\to 2Q$। **श्रृंखला**: $C \\to 2C$। हर एक पर एक जैसा चार्ज. वोल्टेज विभाजित होता है. **समानांतर**: $V$. वही वोल्टेज. चार्ज विभाजित होता है.',
      },
    },
  ],
  next: {
    slug: 'current-electricity',
    title: 'Current Electricity',
  },
};

export default chapter;
